// How a single-page application finds its API.
//
// A SPA plus API application has two host names: the Static Web App the browser
// loads, and the App Service the browser calls. The golden pipeline builds the
// frontend ONCE and deploys that same artifact to every environment, so the API
// address cannot be baked in at build time — the running page has to work it
// out from the host it was served on.
//
// Both ends of that need the same rule. The Domain step uses it to suggest the
// API host, to warn when a pair does not follow the convention, and to refuse a
// Day-2 host the running frontend could never derive. The generated frontend
// carries a copy of `findBase` and `resolveApiBaseUrl` (see
// `src/infrastructure/config/apiBaseUrl.ts` in the SPA skeletons) and uses it at
// run time. Change the rule here and change it there.

/**
 * From one frontend/API pair, the part that comes before `-api`.
 *
 * `lucas-test-app-5-staging` / `lucas-test-app-5-api-staging` -> `lucas-test-app-5`
 *
 * Returns undefined when the pair does not follow the convention, which is not
 * an error: the two host names are whatever DNS was given, and an application
 * whose pair cannot be read simply gets no derivation for environments added
 * later.
 *
 * Every `-api` in the API label is tried, right to left, so an application whose
 * own name contains `api` still resolves.
 */
export function findBase(
  frontendLabel: string,
  apiLabel: string,
): string | undefined {
  for (
    let i = apiLabel.lastIndexOf('-api');
    i >= 0;
    i = i > 0 ? apiLabel.lastIndexOf('-api', i - 1) : -1
  ) {
    if (apiLabel.slice(0, i) + apiLabel.slice(i + 4) === frontendLabel) {
      return apiLabel.slice(0, i);
    }
  }
  return undefined;
}

/** The first label of a host name, and everything after it. */
export function splitHost(host: string): { label: string; zone: string } {
  const [label, ...rest] = host.trim().toLowerCase().split('.');
  return { label: label ?? '', zone: rest.join('.') };
}

/**
 * Environment suffixes a host label can end with, longest first so `prod-eu`
 * is recognised before `eu`. Only these count as a suffix: in
 * `lucas-test-app-5` the `5` is part of the name, and treating it as a suffix
 * suggested `lucas-test-app-api-5`, a host the running frontend never derives.
 */
const ENVIRONMENT_SUFFIXES = [
  'prod-anz',
  'prod-eu',
  'prod-us',
  'prod-au',
  'prerelease',
  'preprod',
  'staging',
  'stage',
  'prod',
  'uat',
  'stg',
  'dev',
  'test',
  'qa',
  'anz',
  'apac',
  'eu',
  'us',
  'au',
];

/**
 * The API host to suggest for a frontend host. A suggestion only: the user can
 * type anything.
 *
 * When other pairs are already known, the suggestion is exactly what the
 * generated frontend will derive at run time (`resolveApiBaseUrl`), so the two
 * can never disagree. With no pair to go on, `-api` goes in front of an
 * environment suffix (`e2a-uat` -> `e2a-api-uat`), or at the end of a label
 * that has none (`app` -> `app-api`, `lucas-test-app-5` -> `lucas-test-app-5-api`).
 */
export function suggestApiHost(
  frontendHost: string,
  knownPairs: Record<string, string> = {},
): string {
  const trimmed = frontendHost.trim().toLowerCase();
  if (!trimmed) return '';

  const derived = resolveApiBaseUrl(trimmed, knownPairs);
  if (derived) return derived.replace(/^https?:\/\//i, '');

  const { label, zone } = splitHost(trimmed);
  const suffix = ENVIRONMENT_SUFFIXES.find(
    candidate =>
      label.endsWith(`-${candidate}`) && label.length > candidate.length + 1,
  );
  const apiLabel = suffix
    ? `${label.slice(0, -(suffix.length + 1))}-api-${suffix}`
    : `${label}-api`;
  return zone ? `${apiLabel}.${zone}` : apiLabel;
}

/**
 * The API origin for a frontend host, from the pairs that are known.
 *
 * An exact match wins. Otherwise the pairs are read for their base, and a host
 * in the SAME zone whose label is that base — or the base plus a suffix — gets
 * the same treatment: `-api` after the base. The zone test is what keeps a
 * different application that happens to share a prefix out of the answer.
 *
 * Returns '' when nothing matches, which is the right answer for localhost and
 * for the default `*.azurestaticapps.net` host: the caller then falls back to a
 * relative URL, which is what the dev server proxy expects.
 */
export function resolveApiBaseUrl(
  hostname: string | undefined,
  table: Record<string, string>,
): string {
  if (!hostname) return '';
  const host = hostname.trim().toLowerCase();
  if (table[host]) return table[host];

  const { label, zone } = splitHost(host);
  for (const [frontendHost, apiOrigin] of Object.entries(table)) {
    const frontend = splitHost(frontendHost);
    if (frontend.zone !== zone) continue;
    const apiHost = apiOrigin.replace(/^https?:\/\//i, '');
    const base = findBase(frontend.label, splitHost(apiHost).label);
    if (base && (label === base || label.startsWith(`${base}-`))) {
      return `https://${base}-api${label.slice(base.length)}.${zone}`;
    }
  }
  return '';
}
